// React
import { memo, useEffect, useCallback } from 'react'

// Components
import { BaseDialog, FormTextArea } from 'components'

// Interfaces
import { IUserRejectRegistrationDialogProps } from './interfaces'

// Utils
import { messageUtils_error } from 'utils/common/message.utils'

// i18n
import { useTranslation } from 'react-i18next'

// Antd
import { Form } from 'antd'

// Custom Hooks
import { useValidation } from 'hooks'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

const UserRejectRegistrationDialog = memo(
  ({ isOpen, loading, onClickFirstButton, onClickCloseButton, attrs }: IUserRejectRegistrationDialogProps) => {
    // Hook
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const { validation_validate } = useValidation()

    // Do when user open the dialog
    useEffect(() => {
      if (isOpen) onClear()

      // eslint-disable-next-line
    }, [isOpen])

    /**
     * @description Clear state from current component
     *
     * @return {void} void
     */
    const onClear = useCallback((): void => {
      form.resetFields()
    }, [form])

    /**
     * @description Watch any change when form is submitted
     *
     * @param {any} form
     *
     * @return {void} void
     */
    const onFinish = useCallback(
      (form: any): void => {
        onClickFirstButton(form)
      },
      [onClickFirstButton],
    )

    /**
     * @description Check before emitting the onClickFirstButton method
     *
     * @return {Promise<void>} Promise<void>
     */
    const onBeforeClickFirstButton = useCallback(async (): Promise<void> => {
      try {
        const response = await form.validateFields()
        const clonedResponse = cloneDeep(response)

        onFinish(clonedResponse)
      } catch (_) {
        messageUtils_error(t('app.validations.formNeedValidation'))
      }
    }, [form, onFinish, t])

    /**
     * @description Render body
     *
     * @return {JSX.Element} JSX.Element
     */
    const renderBody = useCallback((): JSX.Element => {
      return (
        <Form
          form={form}
          name="user-bulk-register-form"
          onFinish={onBeforeClickFirstButton}
          scrollToFirstError
          autoComplete="off"
          requiredMark={false}
        >
          <Form.Item name="reason" rules={validation_validate(['required'], 'user.reject.form.reason')}>
            <FormTextArea placeholder="user.reject.formPlaceholder" />
          </Form.Item>
        </Form>
      )
    }, [form, onBeforeClickFirstButton, validation_validate])

    return (
      <BaseDialog
        title={`${`user.reject.reject`}`}
        isOpen={isOpen}
        closable
        body={renderBody()}
        firstButtonLabel="user.reject.reject"
        onFirstButtonClick={onBeforeClickFirstButton}
        onButtonCloseClick={() => onClickCloseButton(false)}
        isFirstButtonLoading={loading || attrs.isUserActionLoading}
        firstButtonColor="danger"
        isUsingDefaultFooter
        forceRender
        width="673px"
      />
    )
  },
)

export { UserRejectRegistrationDialog }
